import { SetFormActionWithId } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import {
  buildNestedPatch,
  fillBlanks,
  getNestedValue,
} from '@app/finxone-web-frontend/app/lib/utils/nested-object-path.utils';
import { FormActionTypeEnum } from '@finxone-platform/form-action';
import { CompaniesHouseOfficer, LookupsCommonType } from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';

export const COMPANIES_HOUSE_NONE_OF_THE_ABOVE = 'companies_house_none_of_the_above';
export const COMPANIES_HOUSE_NONE_OF_THE_ABOVE_LABEL = 'None of the above / Other';

// Companies House returns officer names in "SURNAME, Forename Middle" form,
// with the surname in all caps. This normalises each name part to a
// human-readable, title-cased form (e.g. "o'brien-smith" -> "O'Brien-Smith").
function toTitleCase(value: string): string {
  return value
    .toLowerCase()
    .replace(
      /(^|[\s'-])([a-z])/g,
      (_match, boundary: string, letter: string) => `${boundary}${letter.toUpperCase()}`,
    );
}

// The API may only provide a single "SURNAME, Forename Middle" string, but
// the onboarding flow and document templates need separate forename, middle
// and surname fields. This normalises the raw value into those components,
// title-cases for display, and leaves them blank for corporate bodies that
// don't follow the natural-person name pattern.
function parseOfficerName(rawName: string | undefined): {
  forename: string;
  middleName: string;
  surname: string;
} {
  const [surnamePart = '', forenamesPart = ''] = (rawName ?? '').split(',').map((part) => part.trim());

  // No comma means this is not a "SURNAME, Forename" natural person name
  // (e.g. a corporate body). Leave all split fields blank.
  if (!forenamesPart) {
    return { forename: '', middleName: '', surname: '' };
  }

  const forenames = forenamesPart.split(/\s+/);
  const forename = forenames[0] ?? '';
  const middleName = forenames.slice(1).join(' ') ?? '';

  return {
    forename: toTitleCase(forename),
    middleName: toTitleCase(middleName),
    surname: toTitleCase(surnamePart),
  };
}

// Builds a clean, human-readable display name ("Forename Middle Surname").
// Prefers the separate name fields returned by the API, falling back to
// parsing the raw Companies House "SURNAME, Forename Middle" format.
export function formatOfficerName(input: CompaniesHouseOfficer | string | undefined): string {
  if (!input) return '';

  if (typeof input === 'string') {
    const name = input.trim();
    if (!name.includes(',')) {
      return toTitleCase(name);
    }
    const { forename, middleName, surname } = parseOfficerName(name);
    const parts = [forename, middleName, surname].filter(Boolean);
    return parts.join(' ');
  }

  const { firstName, middleName, lastName, name } = input;
  const hasApiNameFields = firstName || middleName || lastName;

  if (!hasApiNameFields) {
    const rawName = name ? name.trim() : '';
    if (!rawName.includes(',')) {
      return toTitleCase(rawName);
    }
    const { forename, middleName: parsedMiddle, surname } = parseOfficerName(rawName);
    const parts = [forename, parsedMiddle, surname].filter(Boolean);
    return parts.join(' ');
  }

  const parts = [firstName, middleName, lastName].filter(Boolean);
  return parts.join(' ');
}

export function mapOfficerToFormFields(
  officer: CompaniesHouseOfficer | null | undefined,
): Record<string, string | object | undefined> {
  let forename = '';
  let middleName = '';
  let surname = '';

  if (officer) {
    if (officer.firstName || officer.lastName) {
      forename = officer.firstName ?? '';
      middleName = officer.middleName ?? '';
      surname = officer.lastName ?? '';
    } else {
      const parsed = parseOfficerName(officer.name);
      forename = parsed.forename;
      middleName = parsed.middleName;
      surname = parsed.surname;
    }
  }

  return {
    director: officer?.director ?? '',
    name: officer ? formatOfficerName(officer.name) : '',
    firstName: officer ? forename : '',
    lastName: officer ? surname : '',
    middleName: officer ? middleName : '',
    officerRole: officer?.officerRole ?? '',
    position: officer?.officerRole ?? '',
    appointedOn: officer?.appointedOn ?? '',
    nationality: officer?.nationality ?? '',
    occupation: officer?.occupation ?? '',
    officerBirthMonth:
      officer?.dateOfBirth?.month != null ? String(Number(officer.dateOfBirth.month) - 1) : '',
    officerBirthYear: officer?.dateOfBirth?.year?.toString() ?? '',
    address: officer?.address,
  };
}

/**
 * Maps a raw Companies House officer (as seeded into businessOnboardingFlow.members when a
 * company is selected - see CompaniesHouseListingWidgetComponent) into the field shape the
 * director wizard's array-item Form Widget actually uses, so members[index] hydrates correctly
 * once merged in (see CompaniesHouseDirectorSelectionWidgetComponent.onSelectOfficer). CH's own
 * dateOfBirth/address shape is left in place alongside these - harmless, just unused by the wizard.
 */
export function mapOfficerToDirectorFields(officer: CompaniesHouseOfficer): Record<string, unknown> {
  return {
    name: formatOfficerName(officer.name),
    nationality: officer.nationality ?? '',
    officerBirthMonth:
      officer.dateOfBirth?.month != null ? String(Number(officer.dateOfBirth.month) - 1) : '',
    officerBirthYear: officer.dateOfBirth?.year?.toString() ?? '',
    address: {
      address1: officer.address?.address1 ?? officer.address?.premises ?? '',
      address2: officer.address?.address2 ?? '',
      city: officer.address?.locality ?? officer.address?.city ?? '',
      postalCode: officer.address?.postalCode ?? '',
      country: officer.address?.country ?? '',
    },
  };
}

export function buildOfficerSelectOptions(
  officers: CompaniesHouseOfficer[] | undefined,
): LookupsCommonType[] {
  const officerOptions: LookupsCommonType[] = (officers ?? []).map((officer, index) => ({
    label: formatOfficerName(officer),
    value: `officer-${index}`,
    metadata: officer,
  }));
  return officerOptions;
}

export function dispatchCompaniesHouseSelectedOfficer(
  store: Store,
  officer: CompaniesHouseOfficer | null | undefined,
): void {
  store.dispatch(
    new SetFormActionWithId(
      {
        type: FormActionTypeEnum.BUSINESS_ONBOARDING_FLOW,
        formData: { selectedOfficer: officer ? mapOfficerToFormFields(officer) : null },
      },
      FormActionTypeEnum.BUSINESS_ONBOARDING_FLOW,
    ),
  );
}

/**
 * Seeds businessOnboardingFlow.formData.primaryOfficer from a selected Companies House officer -
 * used by the "Director" dropdown embedded directly on the wizard pages (Name/DOB/Nationality/etc,
 * see DropdownInputFieldComponent.autofillCompanyHouseOfficerFields), which is where the wizard's
 * own primary officer selection happens, as distinct from members[] (the full officer list seeded
 * by CompaniesHouseListingWidgetComponent/CompaniesHouseDirectorSelectionWidgetComponent).
 * fillBlanks (not a plain overwrite) when re-selecting the same officer, so it never clobbers
 * fields the user has already edited on the wizard pages since the last time it was seeded.
 */
export function dispatchPrimaryOfficer(
  store: Store,
  officer: CompaniesHouseOfficer | null | undefined,
): void {
  const rootFormID = FormActionTypeEnum.BUSINESS_ONBOARDING_FLOW;
  const currentFormData = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(rootFormID),
  )?.formData;

  if (!officer) {
    store.dispatch(
      new SetFormActionWithId(
        { type: '', formData: buildNestedPatch(currentFormData, 'primaryOfficer', null) },
        rootFormID,
      ),
    );
    return;
  }

  const currentPrimaryOfficer = getNestedValue<Record<string, unknown>>(currentFormData, 'primaryOfficer');
  const mappedOfficer = mapOfficerToFormFields(officer);
  const isSameOfficerAsBefore =
    !!currentPrimaryOfficer?.['director'] && currentPrimaryOfficer['director'] === mappedOfficer['director'];

  const seededPrimaryOfficer = isSameOfficerAsBefore
    ? fillBlanks(currentPrimaryOfficer, mappedOfficer)
    : mappedOfficer;

  store.dispatch(
    new SetFormActionWithId(
      { type: '', formData: buildNestedPatch(currentFormData, 'primaryOfficer', seededPrimaryOfficer) },
      rootFormID,
    ),
  );
}
